import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { useFlashMessage } from '../context/FlashMessageContext';
import { API_CONFIG } from '../config/api';
import { encryptBiometricImage } from '../utils/biometricEncryption';
import { Bell, X } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { BIOMETRIC_GUIDANCE } from '../constants/biometricCapture';
import BiometricCapture from '../components/BiometricCapture';
import './Dashboard.css';
import './Registration.css';

function getApprovingDeviceFingerprint() {
  const key = 'approving_device_fingerprint';
  let fp = sessionStorage.getItem(key);
  if (!fp) {
    fp = btoa(`${navigator.userAgent}_${window.screen.width}_${window.screen.height}_${navigator.language}`);
    sessionStorage.setItem(key, fp);
  }
  return fp;
}

function BiometricApprovalModal({ session, token, onSuccess, onClose, showError, showSuccess }) {
  const webcamRef = React.useRef(null);
  const [error, setError] = useState(null);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [isUserMismatch, setIsUserMismatch] = useState(false);
  const intervalRef = React.useRef(null);
  const retryCountRef = React.useRef(0);
  const hasSucceededRef = React.useRef(false);

  useEffect(() => {
    if (!session || !token) return;

    const VERIFY_INTERVAL_MS = 2500;
    const MAX_RETRIES = 20;

    const stopInterval = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const runVerification = async () => {
      if (retryCountRef.current >= MAX_RETRIES) {
        setIsTimedOut(true);
        stopInterval();
        return;
      }
      if (!webcamRef.current) return;
      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) return;

      try {
        const base64Image = imageSrc.split(',')[1];
        const { encryptedDataKey, encryptedImageData } = await encryptBiometricImage(
          base64Image,
          API_CONFIG.BASE_URL
        );
        await axios.post(
          `${API_CONFIG.BASE_URL}/user/approve`,
          {
            login_id: session.id,
            decision: 'approve',
            encryptedDataKey,
            encryptedImageData,
            approving_device_fingerprint: getApprovingDeviceFingerprint(),
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (hasSucceededRef.current) return;
        hasSucceededRef.current = true;
        stopInterval();
        showSuccess('Login approved successfully!');
        onSuccess(session.id);
        onClose();
      } catch (err) {
        if (hasSucceededRef.current) return;
        const errCode = err.response?.data?.error;
        const errStatus = err.response?.data?.status;
        if (errCode === 'session_already_processed' && errStatus === 'approved') {
          hasSucceededRef.current = true;
          stopInterval();
          showSuccess('Login approved successfully!');
          onSuccess(session.id);
          onClose();
          return;
        }
        if (errCode === 'biometric_user_mismatch') {
          const msg = 'The face does not match this account. Only the account owner can approve this login.';
          setError(msg);
          showError(msg);
          setIsUserMismatch(true);
          stopInterval();
          return;
        }
        retryCountRef.current += 1;
        const msg = err.response?.data?.error || err.message || 'Verification failed';
        setError(msg);
        showError(msg);
        if (retryCountRef.current >= MAX_RETRIES) {
          setIsTimedOut(true);
          stopInterval();
        }
      }
    };

    const timer = setTimeout(() => {
      runVerification();
      intervalRef.current = setInterval(runVerification, VERIFY_INTERVAL_MS);
    }, 500);

    return () => {
      clearTimeout(timer);
      stopInterval();
    };
  }, [session, token, onSuccess, onClose, showError, showSuccess]);

  return (
    <div className="biometric-modal-overlay" onClick={onClose}>
      <div className="biometric-modal" onClick={(e) => e.stopPropagation()}>
        <div className="biometric-modal-header">
          <h3>Verify Your Identity</h3>
          <button onClick={onClose} className="biometric-modal-close" aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <p className="biometric-modal-description">
          Face verification is required to approve this login request from <strong>{session?.tenant}</strong>.
        </p>
        <div className="biometric-modal-camera biometric-modal-camera-continuous">
          <BiometricCapture
            ref={webcamRef}
            mode="continuous"
            statusText={
              isUserMismatch
                ? 'Face does not match this account. Close and have the account owner approve.'
                : isTimedOut
                  ? 'Verification timed out. Please try again.'
                  : `${BIOMETRIC_GUIDANCE.positioning} Verifying...`
            }
            containerClassName="webcam-container-approval"
          />
        </div>
        {error && <p className="biometric-modal-error">{error}</p>}
      </div>
    </div>
  );
}

function Dashboard() {
  const { user, token, loading } = useContext(AuthContext);
  const { showSuccess, showError } = useFlashMessage();
  const [pendingSessions, setPendingSessions] = useState([]);
  const [biometricModalSession, setBiometricModalSession] = useState(null);

  // Remove manual auth check - now handled by ProtectedRoute
  // useEffect(() => {
  //   console.log('Dashboard - Auth state check:', { loading, user: user ? 'present' : 'null', token: token ? 'present' : 'null' });
  //   
  //   if (!loading && !user) {
  //     console.log('Dashboard - Redirecting to login because user is not authenticated');
  //     navigate('/login');
  //   }
  // }, [user, loading, navigate, token]);

  // Memoized function to prevent unnecessary re-renders
  const fetchPendingSessions = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/user/sessions/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingSessions(response.data || []);
    } catch (error) {
      console.error('Failed to fetch pending sessions:', error);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;

    // Initial fetch
    fetchPendingSessions();
    
    // Set up interval for polling (only for pending sessions)
    const intervalId = setInterval(fetchPendingSessions, 5000);

    return () => clearInterval(intervalId);
  }, [token, fetchPendingSessions]);

  const handleApproval = async (sessionId, approved) => {
    try {
      await axios.post(`${API_CONFIG.BASE_URL}/user/approve`, 
        { login_id: sessionId, decision: approved ? 'approve' : 'deny' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPendingSessions(pendingSessions.filter(session => session.id !== sessionId));
    } catch (error) {
      console.error('Failed to submit approval:', error);
      showError(error.response?.data?.error || 'Failed to submit approval');
    }
  };

  const handleApprovalClick = (session) => {
    if (session.requiresBiometricApproval) {
      setBiometricModalSession(session);
    } else {
      handleApproval(session.id, true);
    }
  };

  const handleBiometricSuccess = (sessionId) => {
    setPendingSessions(pendingSessions.filter(s => s.id !== sessionId));
    setBiometricModalSession(null);
  };

  const handleRejectAll = async () => {
    if (pendingSessions.length === 0) {
      return;
    }

    // Confirm action
    const confirmed = window.confirm(
      `Are you sure you want to reject all ${pendingSessions.length} pending login request(s)?`
    );

    if (!confirmed) {
      return;
    }

    try {
      const response = await axios.post(
        `${API_CONFIG.BASE_URL}/user/sessions/reject-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showSuccess(`Successfully rejected ${response.data.rejected_count} pending request(s)`);
        // Refresh pending sessions list
        fetchPendingSessions();
      }
    } catch (error) {
      console.error('Failed to reject all sessions:', error);
      showError('Failed to reject all sessions. Please try again.');
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  // User is guaranteed to exist due to ProtectedRoute
  if (!user) {
    return null; // This should never happen with ProtectedRoute
  }

  const PendingApprovalsSection = () => (
    <div className="dashboard-section">
      <div className="pending-approvals-header">
        <h2><Bell size={24} /> Pending Approvals</h2>
        {pendingSessions.length > 0 && (
          <button 
            onClick={handleRejectAll} 
            className="reject-all-btn"
            title="Reject all pending login requests"
          >
            Reject All ({pendingSessions.length})
          </button>
        )}
      </div>
      {pendingSessions.length === 0 ? (
        <p>No pending login requests.</p>
      ) : (
        <ul className="pending-approvals-list">
          {pendingSessions.map((session) => (
            <li key={session.id} className="approval-item">
              <p>
                Login request from <strong>{session.tenant}</strong>
              </p>
              <div className="approval-actions">
                <button onClick={() => handleApprovalClick(session)} className="approve-btn">Approve</button>
                <button onClick={() => handleApproval(session.id, false)} className="deny-btn">Deny</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <DashboardLayout activeNav="dashboard">
      <PendingApprovalsSection />

      {biometricModalSession && (
        <BiometricApprovalModal
          session={biometricModalSession}
          token={token}
          onSuccess={handleBiometricSuccess}
          onClose={() => setBiometricModalSession(null)}
          showError={showError}
          showSuccess={showSuccess}
        />
      )}
    </DashboardLayout>
  );
}

export default Dashboard;